import { BookOpen, Compass, Globe, Link2, Sparkles, Waypoints, Wrench } from 'lucide-react';

import { EVERY_ROLE_SURFACE } from './sidebar-definition.types';

import type { SidebarDefinition } from './sidebar-definition.types';

/**
 * The AGENT group's nav rows — the surfaces an operator configures the agent
 * ITSELF from, as opposed to the surfaces a visitor meets or the measurements
 * taken of them.
 *
 * Split out of `sidebar-definitions.ts` at the ~300-line ceiling
 * (`.claude/rules/architecture.md`), on the seam that file already draws: it
 * spreads `WORKSPACE_SIDEBAR_DEFINITIONS` from a sibling for the same reason,
 * so this is the second application of one pattern rather than a new one. The
 * group is the seam because a row's group is what a reader scans by, and each
 * row's justification is written for its neighbours in that group.
 *
 * The ORDER within the exported array is the order the group renders, and the
 * array is spread where the block used to sit, so nothing about the rendered
 * nav moves.
 */

export const AGENT_SIDEBAR_DEFINITIONS: readonly SidebarDefinition[] = [
  // WF-5.3 (D6) — mapping LEADS the group, because it is how a workspace gets an
  // agent that knows anything at all. The tools catalog below is where the
  // RESULT lands, so a nav that opened with it put the outcome above the act
  // that produces it — and for the widget-only customer this product is built
  // for, mapping is the only act there is.
  //
  // EVERY ROLE. Every other row here that touches walk data carries a real
  // `surface`, and this one must not: `/site-walk` is where a workspace with NO
  // data goes to get some, so gating it on a read permission over data that does
  // not exist yet would hide the row from exactly the operator it exists for. The
  // page's own start mutation is `catalog:edit`-gated server-side, which is the
  // real boundary, and the page states its own gate rather than vanishing.
  {
    to: '/site-walk',
    labelKey: 'sidebar.siteWalk',
    icon: Compass,
    group: 'AGENT',
    surface: EVERY_ROLE_SURFACE,
  },

  // The unified Tools Catalog (`/tools`) — the SINGLE front door of the AGENT
  // category and the one place every capability lives (app-menu restructure). It
  // absorbed the former standalone /catalog browse surface AND the external-MCP
  // (BYOMCP) server registry (the "External MCP servers" section on the page);
  // filter the list to a source (e.g. External/MCP) to manage just those
  // endpoints, and open any row's drawer to edit its params. The old /catalog +
  // /byomcp paths redirect here. Governance / availability / manual + page-action
  // tooling still live in the hub drawer + master controls.
  { to: '/tools', labelKey: 'sidebar.tools', icon: Wrench, group: 'AGENT', surface: 'catalog' },
  {
    to: '/prompts',
    labelKey: 'sidebar.prompts',
    icon: Sparkles,
    group: 'AGENT',
    surface: 'catalog',
  },
  // The unified Documentation hub (`/docs`) — RAG docs SOURCES config + the
  // auto-generated API REFERENCE under one tabbed roof (the former standalone
  // `/documentation` SURFACES row folded into its "API reference" tab). One AGENT
  // row replaces the two former separate ones rather than costing AGENT a slot.
  // DE-006 — named on its OWN surface rather than riding `catalog`. The row now
  // answers to the same grant its handlers do, so the `documentation` role sees
  // it and a future role without that grant loses the row instead of finding an
  // empty page.
  {
    to: '/docs',
    labelKey: 'sidebar.documentation',
    icon: BookOpen,
    group: 'AGENT',
    surface: 'documentation',
  },
  // The outbound read allowlist. A sibling of `/docs` rather than an ORG setting,
  // and the pairing is the point: `/docs` is what the agent knows from YOUR
  // documentation, this is what it may read from the WEB. Homing it under
  // settings would file the agent's only external reach next to billing and API
  // keys, where nobody configuring the agent would look for it.
  {
    to: '/settings/allowed-sites',
    labelKey: 'sidebar.allowedSites',
    icon: Globe,
    group: 'AGENT',
    surface: 'settings',
  },
  // Agent Impact Intelligence (IMP-3 / IMP-7) — the agent's own performance
  // surfaces: /impact (deflection / outcome / ROI analytics) and /map (the
  // capability-interaction graph). They live in AGENT beside the agent's
  // configuration (tools catalog / prompts / docs) — "everything about your
  // agent" in one group — which keeps the merge's OBSERVABILITY group (now
  // carrying main's /users) exactly at the ≤6 scannable cap (A17). Operator may
  // relocate.
  //
  // Both are gated `analytics:view` server-side (`insights.resolver.ts:162`,
  // `site-graph.resolver.ts:70`, `site-pulse.resolver.ts:50`), so both carry the
  // matching `surface` (F15). Without it a role below the analytics bar was
  // offered two nav rows whose every query 403s — the exact "never land them on
  // an error state" case this annotation exists for.
  { to: '/map', labelKey: 'sidebar.map', icon: Waypoints, group: 'AGENT', surface: 'analytics' },

  // The unified Connections hub — the per-tenant MCP server, identity
  // federation, LLM provider keys (BYOK), widget origins and the custom domain,
  // each a tab inside it; the former standalone nav rows are retired and their
  // paths redirect to the matching tab.
  //
  // In AGENT since 2026-08-28, on the reasoning `/settings/allowed-sites`
  // already carries: this is the agent's external reach — where it
  // serves from, which keys it speaks with, which origins may embed it — and
  // filing it under settings puts it next to billing and API keys, where
  // nobody configuring the agent would look. It had a group of its OWN until
  // that date, which by then was a heading over a single row repeating it (see
  // `SidebarGroup`). AGENT is now 6, exactly at the A17 ≤6 scannable cap.
  {
    to: '/connections',
    labelKey: 'sidebar.connections',
    icon: Link2,
    group: 'AGENT',
    surface: 'integrations',
  },
];
